import {
    arendaRadioguideCalculatorRawSetting,
    arendaRadioguideGetPrice,
    arendaRadioguideUpdaters,
    getArendaRadioguideCalculatorSetting,
} from '~/services/calculator-arenda-radioguide.service'
import {
    audioguideCalculatorRawSetting,
    audioguideFieldTitles,
    audioguideGetPrice,
    audioguideUpdaters,
} from '~/services/calculator-audioguide.service'
import {
    cabinCalculatorRawSetting,
    cabinFieldTitles,
    cabinGetPrice,
    cabinUpdaters,
} from '~/services/calculator-cabin.service'
import {
    conferenceSystemCalculatorRawSetting,
    conferenceSystemFieldTitles,
    conferenceSystemGetPrice,
    conferenceSystemUpdaters,
} from '~/services/calculator-conference-system.service'
import {
    radioguideCalculatorRawSetting,
    radioguideFieldTitles,
    radioguideGetPrice,
    radioguideUpdaters,
} from '~/services/calculator-radioguide.service'
import {
    sinhroperevodCalculatorRawSetting,
    sinhroperevodFieldTitles,
    sinhroperevodGetPrice,
    sinhroperevodUpdaters,
} from '~/services/calculator-sinhroperevod.service'
import type {
    CalculatorGetPriceFunctions,
    CalculatorSettings,
    CalculatorUpdaters,
    ICalculator,
    ICalculatorOption,
    ICalculatorPrice,
    ICalculatorSelectedValues,
    ICalculatorSetting,
    ICalculatorSettingField,
    IFieldTitlesByType,
    TCalculatorType,
} from '~/types/calculator.types'
import {
    DirectionsSlugAsConst,
    type TDirectionSlug,
} from '~/types/global.types'

const settings: CalculatorSettings = {
    'arenda-radioguides': arendaRadioguideCalculatorRawSetting,
    radioguide: radioguideCalculatorRawSetting,
    audioguide: audioguideCalculatorRawSetting,
    cabin: cabinCalculatorRawSetting,
    sinhroperevod: sinhroperevodCalculatorRawSetting,
    'conference-system': conferenceSystemCalculatorRawSetting,
}

const fieldTitles: IFieldTitlesByType = {
    'arenda-radioguides': {},
    radioguide: radioguideFieldTitles,
    audioguide: audioguideFieldTitles,
    cabin: cabinFieldTitles,
    sinhroperevod: sinhroperevodFieldTitles,
    'conference-system': conferenceSystemFieldTitles,
}

const updaters: CalculatorUpdaters = {
    'arenda-radioguides': arendaRadioguideUpdaters,
    radioguide: radioguideUpdaters,
    audioguide: audioguideUpdaters,
    cabin: cabinUpdaters,
    sinhroperevod: sinhroperevodUpdaters,
    'conference-system': conferenceSystemUpdaters,
}

const getPriceFunctions: CalculatorGetPriceFunctions = {
    'arenda-radioguides': arendaRadioguideGetPrice,
    radioguide: radioguideGetPrice,
    audioguide: audioguideGetPrice,
    cabin: cabinGetPrice,
    sinhroperevod: sinhroperevodGetPrice,
    'conference-system': conferenceSystemGetPrice,
}

/**
 * Отдает настройку калькулятора в соостветствии с типом
 *
 * Определяет нужный сеттинг для калькутора по типу и добавляет поля калькулятора, приводя их в нужный вид
 *
 * @param { TDirectionSlug } type тип калькулятора
 * @param { ICalculator } calculator данные калькулятора
 *
 * @return { ICalculatorSetting } настройки калькулятора
 */
export function getCalculator(
    type: TCalculatorType,
    calculator: ICalculator | null
): ICalculatorSetting | null {
    if (type === 'arenda-radioguides') {
        return getArendaRadioguideCalculatorSetting(calculator)
    }

    if (
        !DirectionsSlugAsConst.includes(type as TDirectionSlug) ||
        !calculator ||
        !updaters[type] ||
        !getPriceFunctions[type]
    )
        return null

    const setting: ICalculatorSetting = JSON.parse(
        JSON.stringify({ ...settings[type] })
    )

    const calculatorFieldSlugs: string[] = Object.keys(calculator).filter(
        (slug: string) => !['name', 'prices', 'products'].includes(slug)
    )

    const fields: ICalculatorSettingField[] = []

    calculatorFieldSlugs.forEach((slug: string) => {
        const options = (calculator[slug] as { data?: ICalculatorOption[] })
            ?.data

        if (
            typeof calculator[slug] === 'object' &&
            Array.isArray(options) &&
            fieldTitles[type]
        ) {
            fields.push({
                title: fieldTitles[type][slug],
                slug,
                options,
                default_disabled: false,
                default_hidden: false,
            })
        }
    })

    if (calculator.prices?.length) {
        calculator.prices.forEach((price: ICalculatorPrice) => {
            setting.prices[price.calculator_variable_name] = price.price
        })
    }

    setting.fields.push(...fields)

    setting.updaters = updaters[type]
    setting.getPrice = getPriceFunctions[type]

    return setting
}

/**
 * Отдает объект с ценами выбранных значений полей, а также для статичный товаров, которые учавствуют в формуле итоговой цены
 *
 * Собирает цены для выбраныых значений полей и статичных товаров
 *
 * @param { ICalculatorSetting } calculator тип калькулятора
 * @param { ICalculatorSelectedValues } selectedValues данные калькулятора
 *
 * @return { { [key: string]: number } } объект с ценами
 */
export function getCalculatorProductsPrice(
    calculator: ICalculatorSetting,
    selectedValues: ICalculatorSelectedValues
): { [key: string]: number } {
    const productPrices: { [key: string]: number } = {}

    for (const field of calculator.fields) {
        const modificator =
            calculator.price_modificators[field.slug]?.[
                selectedValues[field.slug]
            ] || 1

        if ('options' in field) {
            const price: number =
                field.options?.find(
                    (option: ICalculatorOption) =>
                        option.calculator_variable_name ===
                        selectedValues[field.slug]
                )?.product?.data.price || 0
            productPrices[field.slug] = price * modificator
        }
    }

    for (const slug of Object.keys(calculator.prices)) {
        productPrices[slug] = calculator.prices[slug]
    }

    return productPrices
}

export function getCalculatorTitle(directionSlug: TCalculatorType) {
    switch (directionSlug) {
        case 'radioguide':
        case 'arenda-radioguides': {
            return 'Рассчитайте стоимость Вашего комплекта радиогидов'
        }
        case 'audioguide': {
            return 'Рассчитайте стоимость Вашего комплекта аудиогидов'
        }
        case 'cabin': {
            return 'Рассчитайте стоимость Вашего заказа кабин'
        }
        case 'sinhroperevod': {
            return 'Рассчитайте стоимость Вашего комплекта синхроперевода'
        }
        case 'conference-system': {
            return 'Рассчитайте стоимость Вашей конференц-системы'
        }
        case 'dostupsreda': {
            return 'Рассчитайте стоимость Вашего комплекта'
        }
        default: {
            return 'Калькулятор'
        }
    }
}
